import React, { useContext } from "react";
import { Box, Grid, Link, Paper as MuiPaper, Theme, Tooltip, useMediaQuery } from "@mui/material";
import EyeBrow from "component/MarketPlace/eyeBrow/eyeBrow";
import { ThemeContext } from "styled-components";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorIcon from "@mui/icons-material/Error";
import Typography from "component/MarketPlace/Typography";
import { ApprovalIcon } from "component/commonComponents/Icon";
import ContentDescription from "./ContentDescription";
import TimeUpdated from "./TimeUpated";
import { mapColorEyeBrow } from "utils/mapColorEyeBrow";
import { ResourceType, TicketStatus } from "./types";
import DateUtils from "utils/date";
import { StyledTooltip } from "component/cardComponent/pcCard/pcCard.style";
import { TruncatedText } from "component/MarketPlace/Typography/Typography.style";
import { StyledSubStageEyeBrow } from "component/MarketPlace/OrderItemCard/index.style";
import { useCustomLayout, useHyperlinkStyle } from "component/layoutComponents/CustomLayout/CustomLayoutContext";

export interface ITicketCardProps {
  infoTicket: any;
  createdDate?: string;
  updatedDate: string;
  locale: {
    ticket: {
      OPEN: string;
      CLOSED: string;
    };
    requester: string;
    viewDetails: string;
    approver: string;
    approved: string;
    created: string;
    updated: string;
    delegated: string;
    asset: object;
    rejectedBy: string;
  };
  onClickViewDetail: () => {};
  actions: any;
  tooltipStatusMessage?: any;
  ticketType: string;
}

const UNAVAIABLED = "N/A";

const TicketCard = (props: ITicketCardProps) => {
  const theme = useContext(ThemeContext);
  const {
    infoTicket,
    createdDate,
    updatedDate,
    onClickViewDetail,
    locale,
    actions,
    tooltipStatusMessage,
    ticketType,
  } = props;
  let state = infoTicket?.state;
  let requestItem = infoTicket?.requestItem;
  let approver = infoTicket?.approvalBy;
  const assetTicketLocale: { [key: string]: string } = {...locale?.asset, ...locale?.ticket};
  const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isMdDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const requester =
    ticketType === ResourceType.Approvals
      ? (requestItem?.variables?.json_storage?.requestor_name || requestItem?.openBy)
      : (ResourceType.Incidents === ticketType ? infoTicket?.requestFor : infoTicket?.openBy);
  const isCheckSubStage: boolean = ((ResourceType.Requests === ticketType &&
    infoTicket?.variables?.stage?.display_value) ||
    (ResourceType.Approvals === ticketType &&
      infoTicket?.originalState) ||
    (ResourceType.Incidents === ticketType && state));
  const handleMouseDown = (e: any) => {
    if (e.button === 0) {
      onClickViewDetail();
    }
  };

  const { hyperlinkStyle } = useHyperlinkStyle();
  const customLayoutConfig = useCustomLayout();
  const { isUseHyperlinkStyle } = customLayoutConfig;

  const renderOriginalState = (requestType: string, isMapColor: boolean) => {
    let text;
    switch (requestType) {
      case ResourceType.Approvals:
        text = infoTicket?.originalState;
        break;
      case ResourceType.Incidents:
        text = state?.toUpperCase() === "OPEN" ? "SUBMITTED" : "RESOLVED";
        break;
      case ResourceType.Requests:
        text = infoTicket?.variables?.stage?.display_value;
        break;
      default:
        text = "";
        break;
    }
    return isMapColor
      ? text?.toUpperCase()
      : assetTicketLocale[text?.toUpperCase()]?.toUpperCase() || text?.toUpperCase();
  };

  const eyeBrowOriginalStateColor = mapColorEyeBrow(
    renderOriginalState(ticketType, true),
    theme
  );

  const renderState = (requestType: string, isMapColor: boolean) => {
    let text;
    switch (requestType) {
      case ResourceType.Approvals:
        text = isMapColor
          ? state?.toUpperCase()
          : assetTicketLocale[state?.toUpperCase()]?.toUpperCase() +
            (infoTicket?.approver?.delegate ? ` - ${locale?.delegated}` : "");
        break;
      case ResourceType.Incidents:
        text = isMapColor
          ? state?.toUpperCase() === "RESOLVED"
            ? "OPEN"
            : state
          : state?.toUpperCase() === "RESOLVED"
          ? assetTicketLocale["OPEN"]?.toUpperCase()
          : assetTicketLocale[state?.toUpperCase()]?.toUpperCase();
        break;
      case ResourceType.Requests:
        text = isMapColor ? state?.toUpperCase() : assetTicketLocale[state?.toUpperCase()]?.toUpperCase();
        break;
      default:
        text = "";
        break;
    }
    return text?.toUpperCase();
  };

  const eyeBrowStateColor = mapColorEyeBrow(
    renderState(ticketType, true),
    theme
  );

  const tooltipMessage = (status: string) => {
    let message;
    switch (status) {
      case TicketStatus.OPEN:
        message = tooltipStatusMessage?.open;
        break;
      case TicketStatus.CLOSED:
        message = tooltipStatusMessage?.closed;
        break;
      case TicketStatus.AWAITING_APPROVAL:
        message = tooltipStatusMessage?.awaitingApproval;
        break;
      case TicketStatus.AWAITING_APPROVAL_DELEGATED:
        message = tooltipStatusMessage?.awaitingApprovalDelegated;
        break;
      case TicketStatus.APPROVED:
        message = tooltipStatusMessage?.approved;
        break;
      case TicketStatus.APPROVED_DELEGATED:
        message = tooltipStatusMessage?.approvedDelegated;
        break;
      case TicketStatus.REJECTED:
        message = tooltipStatusMessage?.rejected;
        break;
      case TicketStatus.REJECTED_DELEGATED:
        message = tooltipStatusMessage?.rejectedDelegated;
        break;
      case TicketStatus.PREPARING_DEVICE:
        message = tooltipStatusMessage?.preparingDevice;
        break;
      case TicketStatus.CANCELLED:
        message = tooltipStatusMessage?.cancelled;
        break;
      case TicketStatus.RESOLVED:
        message = tooltipStatusMessage?.resolved;
        break;
      case TicketStatus.DEVICEINTRANSIT:
        message = tooltipStatusMessage?.deviceInTransit;
        break;
      case TicketStatus.INPROGRESS:
        message = tooltipStatusMessage?.inProgress;
        break;
      case TicketStatus.NOLONGERREQUIRED:
        message = tooltipStatusMessage?.noLongerRequired;
        break;
      case TicketStatus.PENDINGCANCELLATION:
        message = tooltipStatusMessage?.pendingCancellation;
        break;
      case TicketStatus.SUBMITTED:
        message = tooltipStatusMessage?.submitted;
        break;
      case TicketStatus.UNDERINVESTIGATION:
        message = tooltipStatusMessage?.underInvestigation;
        break;
      case TicketStatus.APPROVAL_REJECTED:
        message = tooltipStatusMessage?.approvalRejected;
        break;
      case TicketStatus.PCRETURN:
        message = tooltipStatusMessage?.pcReturn;
        break;
      case TicketStatus.ORDERING_DEVICE:
        message = tooltipStatusMessage?.orderingDevice;
        break;
      case TicketStatus.PREPARING_SHIPPING_MATERIALS:
        message = tooltipStatusMessage?.preparingShippingMaterials;
        break;
      case TicketStatus.OEM_SUBMITTED:
        message = tooltipStatusMessage?.oemSubmitted;
        break;
      case TicketStatus.OEM_COMPLETED:
        message = tooltipStatusMessage?.oemCompleted;
        break;
      case TicketStatus.OEM_ACKNOWLEDGED:
        message = tooltipStatusMessage?.oemAcknowledged;
        break;
      case TicketStatus.OEM_DELIVERED:
        message = tooltipStatusMessage?.oemDelivered;
        break;
      case TicketStatus.OEM_SHIPPED:
        message = tooltipStatusMessage?.oemShipped;
        break;
      default:
        message = "";
        break;
    }
    return message;
  };

  return (
    <MuiPaper
      variant="outlined"
      sx={{
        borderRadius: `${theme.uptimeSpace[4]}px`,
        "&:hover": {
          boxShadow:
            "0 1px 2px -2px rgba(0, 0, 0, 0.16),  0 3px 6px 0 rgba(0, 0, 0 , 0.12), 0 5px 12px 4px rgba(0, 0, 0, 0.09) !important",
        },
        width: "100%"
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        padding={`${theme.uptimeSpace[8]}px`}
        gap="15px 0"
        data-ritm_number={infoTicket?.number || requestItem?.number}
      >
        <Grid
          container
          display="flex"
          alignItems="center"
          gap={{ xs: "8px 0", md: "0px" }}
        >
          <Grid 
            item 
            xs={12} 
            sm={12} 
            md={4} 
            lg={4}
            minHeight="48px"
            display="flex"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap="0 10px">
              <Box
                width="38px"
                height="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ApprovalIcon color={theme.uptimeColors.generalColors.icon.enabled} />
              </Box>
              <StyledTooltip title={
                ticketType === ResourceType.Approvals
                ? requestItem?.shortDescription
                : infoTicket?.shortDescription
                } placement="bottom-start">
                <TruncatedText
                  maxlines={1}
                  onClick={() => onClickViewDetail()}
                  variant="overline"
                  sx={{
                    fontSize: `${theme.uptimeSpace[12]}px`,
                    fontWeight: '600',
                    lineHeight: '30px',
                    color: 'text.primary',
                    cursor: "pointer",
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                  id="lbl-ticketcard-short_description"
                >
                  {ticketType === ResourceType.Approvals
                  ? requestItem?.shortDescription
                  : infoTicket?.shortDescription}
                </TruncatedText>
              </StyledTooltip>
            </Box>
            {isMdDown && (
              <Box alignSelf="center">
                {actions}
              </Box>
            )}
          </Grid>
          <Grid 
            item 
            xs={12} 
            sm={12} 
            md={6}
            lg={6}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              px={2}
              py={0}
            >
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                minWidth={{ xs: "auto", md: "auto", lg: "auto" }}
                maxWidth={{ xs: "unset", md: "120px", lg: "160px" }}
              >
                <EyeBrow
                  variant="filled"
                  sx={{
                    backgroundColor: eyeBrowStateColor?.background,
                    color: eyeBrowStateColor?.color,
                    fontWeight: 600,
                    height: `${theme.uptimeSpace[12]}px`,
                    lineHeight: `${theme.uptimeSpace[9]}px`,
                    fontSize: `${theme.uptimeSpace[6]}px`,
                  }}
                  label={renderState(ticketType, false)}
                  endIcon={
                    tooltipMessage(renderState(ticketType, true)) && (
                      <Tooltip
                        title={tooltipMessage(renderState(ticketType, true))}
                      >
                        <Box display="flex">
                          <ErrorIcon
                            sx={{ color: "#0000008c", width: `${theme.uptimeSpace[8]}px`, height: `${theme.uptimeSpace[8]}px` }}
                          />
                        </Box>
                      </Tooltip>
                    )
                  }
                  id="lbl-ticketcard-status"
                  data-ritm_number={requestItem?.number || infoTicket?.number}
                />
              </Box>
              {isCheckSubStage && (
                <>
                  <ChevronRightIcon
                    sx={{ width: { xs: "24px", sm: "60px" }, height: "24px" }}
                  />
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <StyledSubStageEyeBrow
                      sx={{
                        backgroundColor: eyeBrowOriginalStateColor?.background,
                        padding: "3px 4px",
                        fontWeight: 600,
                        height: `${theme.uptimeSpace[12]}px`,
                        lineHeight: `${theme.uptimeSpace[9]}px`,
                        fontSize: `${theme.uptimeSpace[6]}px`,
                        color: eyeBrowOriginalStateColor?.color,
                        whiteSpace: 'unset'
                      }}
                      label={renderOriginalState(ticketType, false)}
                      variant="filled"
                      endIcon={
                        tooltipMessage(
                          renderOriginalState(ticketType, true)
                        ) && (
                          <Tooltip
                            title={tooltipMessage(
                              renderOriginalState(ticketType, true)
                            )}
                          >
                            <Box display="flex">
                              <ErrorIcon
                                sx={{ color: "#0000008c", width: `${theme.uptimeSpace[8]}px`, height: `${theme.uptimeSpace[8]}px` }}
                              />
                            </Box>
                          </Tooltip>
                        )
                      }
                      id="lbl-ticketcard-status_ticket_type"
                      data-ritm_number={requestItem?.number || infoTicket?.number}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={2}
            lg={2}
            display="flex"
            justifyContent={{ xs: 'space-between', sm: 'space-between', md: 'end' }}
            alignItems="center"
            gap="0 10px"
          >
            <Box alignSelf="start" paddingRight="20px" display={{ sm: 'block', md: 'none' }}>
              <Typography
                fontSize="14px"
                variant="overline"
                color="text.secondary"
                id="lbl-ticketcard-number_sm"
              >
                {ticketType === ResourceType.Approvals
                  ? `#${requestItem?.number}`
                  : `#${infoTicket?.number}`}
              </Typography>
            </Box>
            <Link
              onMouseDown={handleMouseDown}
              sx={{ cursor: "pointer", textDecoration: "none", ...hyperlinkStyle }}
              id="btn-ticketcard-viewdetail"
              data-ritm_number={requestItem?.number || infoTicket?.number}
              role="link"
              tabIndex={0}
              aria-label="View Details"
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                  '&:hover': {
                    color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                    textDecoration: isUseHyperlinkStyle ? 'underline' : 'none'
                  }
                }} 
                fontWeight={600}
                id="lbl-ticketcard-view_detail"
              >
                {locale?.viewDetails}
              </Typography>
            </Link>
            {isMdUp && (
              <Box>
                {actions}
              </Box>
            )}
          </Grid>
        </Grid>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          width={{sm: "40%", md: "100%"}}
        >
          <Box display="flex" alignItems="center">
            <Box alignSelf="start" paddingRight="20px" display={{ xs: 'none', sm: 'none', md: 'block' }}>
              <Typography
                fontSize="14px"
                variant="overline"
                color="text.secondary"
                id="lbl-ticketcard-number_md"
              >
                {ticketType === ResourceType.Approvals
                  ? `#${requestItem?.number}`
                  : `#${infoTicket?.number}`}
              </Typography>
            </Box>
            <Box 
              display="flex" 
              flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
              alignItems={{ xs:' flex-start', sm: 'flex-start' }}
            >
              <ContentDescription
                label={locale?.requester?.toUpperCase()}
                name={requester || UNAVAIABLED}
              />
              {approver?.name && ticketType !== ResourceType.Incidents && (
                <ContentDescription
                  label={
                    infoTicket?.originalState?.toUpperCase() === TicketStatus.REJECTED ||
                    infoTicket?.variables?.stage?.display_value?.toUpperCase() === TicketStatus.REJECTED
                      ? locale?.rejectedBy?.toUpperCase()
                      : locale?.approver?.toUpperCase()
                  }
                  name={approver?.name || UNAVAIABLED}
                />
              )}
            </Box>
          </Box>
          <Box 
            display="flex"
            alignItems={{ xs:' flex-start', sm: 'flex-start', md: 'center' }}
            flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          >
            <TimeUpdated label={locale?.created?.toUpperCase()} date={DateUtils.formatDate(createdDate, "ll")} />
            {state?.toUpperCase() === TicketStatus.CLOSED && (
              <TimeUpdated
                label={assetTicketLocale[TicketStatus.CLOSED]?.toUpperCase()}
                date={DateUtils.formatDate(updatedDate, "ll")}
              />
            )}
            <TimeUpdated label={locale?.updated?.toUpperCase()} date={DateUtils.timetoXbyGMT(updatedDate)} />
          </Box>
        </Box>
      </Box>
    </MuiPaper>
  );
};

export default TicketCard;
